import type {
  HealthcareService,
  Location,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { now } from '@seen/core/utils'
import type {
  EngineSlot,
  SchedulingRuleInfoMap,
  SuggestEngineSlotsRequest,
} from '@seen/request-types/schedulingRules'
import type { UseQueryResult } from '@tanstack/react-query'
import { addWeeks, max, startOfDay } from 'date-fns'
import { useMemo } from 'react'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

// How far out the engine may place candidates. Matches the AI suggester's
// horizon so switching between the two does not change which weeks are in
// scope, only how the candidates within them are chosen.
const SEARCH_HORIZON_WEEKS = 6

// Ranked candidates requested per generation. The route caps at 100; this is
// deep enough that the scroll list still has options after a week or two is
// fully booked.
const MAX_SLOTS = 60

// Engine responses are deterministic for a given window and fact set, so a
// brief stale window absorbs the re-renders of typing in the form without
// refetching on every keystroke.
const STALE_TIME_MS = 30_000

export interface EngineRecommendedSlots {
  hasConfig: boolean
  slots: EngineSlot[]
  rules: SchedulingRuleInfoMap
}

export function useEngineRecommendedSlots({
  patient,
  healthcareService,
  practitioner,
  location,
  durationMinutes,
  searchFrom,
  excludeAppointmentId,
  enabled = true,
}: {
  patient: Reference<Patient> | undefined
  healthcareService: Reference<HealthcareService> | undefined
  practitioner: Reference<Practitioner> | undefined
  location?: Reference<Location>
  // The operator's chosen visit length, when it overrides the visit type's own
  // average; the engine re-ranks candidates at this duration.
  durationMinutes?: number
  // Earliest acceptable appointment date, derived from the calendar's visible
  // horizon. Undefined searches from today.
  searchFrom?: string
  // The visit being moved. The engine ignores it in counts and conflicts.
  excludeAppointmentId?: string
  // False keeps the query inert — the caller's flag is off, so nothing should
  // reach the engine route.
  enabled?: boolean
}): UseQueryResult<EngineRecommendedSlots | undefined> {
  // Mirrors /schedule's readiness gate. The slots route requires a service and
  // at least one practitioner, so firing while the form is half-filled sends a
  // request that cannot succeed; the patient is required too, since candidates
  // are ranked against that participant's own facts.
  const { ai } = useSeenApi()

  // Day granularity on purpose: nudging the time within the selected day must
  // not rekey the query, and a date already behind us cannot be booked, so the
  // window never starts before today.
  const { searchStart, searchEnd } = useMemo(() => {
    const today = startOfDay(now())
    const start = max([searchFrom ? startOfDay(searchFrom) : today, today])
    return {
      searchStart: start.toISOString(),
      searchEnd: addWeeks(start, SEARCH_HORIZON_WEEKS).toISOString(),
    }
  }, [searchFrom])

  const practitionerReference = practitioner?.reference
  const serviceReference = healthcareService?.reference
  const patientReference = patient?.reference
  const locationReference = location?.reference
  const excludedAppointment = excludeAppointmentId ?? null

  const isReady =
    !!ai && !!patientReference && !!serviceReference && !!practitionerReference

  const result = useQuery({
    // `ai` is the host-injected capability, not query input: it is stable for
    // the app's lifetime and keying on it would churn the cache for nothing.
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'engineRecommendedSlots',
      patientReference,
      serviceReference,
      practitionerReference,
      locationReference,
      excludedAppointment,
      searchStart,
      searchEnd,
      durationMinutes,
    ],
    enabled: enabled && isReady,
    staleTime: STALE_TIME_MS,
    // Picking a specific time sets `durationMinutes`, changing this query's
    // key. Held across that refetch (instead of clearing to undefined) so a
    // caller gating mount on "no data yet" doesn't unmount
    // `EngineRecommendedSlots` for the refetch window, clearing its mirrored
    // grid outlines and flashing a skeleton over slots already on screen.
    //
    // Plain `keepPreviousData` holds the prior result across ANY key change,
    // not just duration — a patient/practitioner/service/location switch would
    // keep painting the OLD context's candidates as if they were the new
    // context's answer. Only hold data when the key's identity portion
    // (patient/practitioner/service/location/window) is unchanged from the
    // previous query; a real context switch drops straight to `undefined` so
    // callers correctly treat it as unresolved rather than a trustworthy
    // stand-in.
    placeholderData: (previousData, previousQuery) => {
      if (!previousQuery) {
        return undefined
      }
      const [
        ,
        prevPatient,
        prevService,
        prevPractitioner,
        prevLocation,
        prevExcluded,
        prevStart,
        prevEnd,
      ] = previousQuery.queryKey
      const identityUnchanged =
        prevPatient === patientReference &&
        prevService === serviceReference &&
        prevPractitioner === practitionerReference &&
        prevLocation === locationReference &&
        prevExcluded === excludedAppointment &&
        prevStart === searchStart &&
        prevEnd === searchEnd
      return identityUnchanged ? previousData : undefined
    },
    queryFn: async (): Promise<EngineRecommendedSlots> => {
      // Unreachable: `isReady` gates the query on all four being present.
      // Narrowing rather than asserting keeps the request well-formed.
      invariant(
        ai && patientReference && serviceReference && practitionerReference,
        'engine slots query ran without a complete request context'
      )

      const request: SuggestEngineSlotsRequest = {
        patient: { reference: patientReference },
        practitioners: [{ reference: practitionerReference }],
        healthcareService: { reference: serviceReference },
        windowStart: searchStart,
        windowEnd: searchEnd,
        maxSlots: MAX_SLOTS,
        durationMinutes,
        location: locationReference
          ? { reference: locationReference }
          : undefined,
      }
      if (excludedAppointment) {
        request.excludeAppointmentId = excludedAppointment
      }
      const response = await ai.suggestEngineSchedulingSlots(request)

      return {
        hasConfig: response.hasConfig,
        slots: response.slots,
        rules: response.rules,
      }
    },
  })

  useErrorToast(
    result.error &&
      `Error generating appointment slots: ${result.error.message ?? 'Unknown error'}`
  )

  return result
}
